import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CookieJs from "js-cookie";
import { get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import contractService from "../../services/contract";
import queryDefault from "../../utils/queryDefault";

export default function ContractForm() {
  const { id } = useParams();
  const user = JSON.parse(CookieJs.get("USER") ?? "");
  const notification = useNotification();
  const navigate = useNavigate();
  const [pdfFile, setPdfFile] = useState<File[]>([]);

  const createContract = useMutation(
    "create-contract",
    contractService.createContract,
    {
      onSuccess: (response) => {
        notification.onOpen({
          message:
            response.message === ""
              ? "Contract was successfully created!"
              : response.message,
          type: "success",
          position: "top",
        });
        navigate("/app/contract");
      },
      onError: (error: any) => {
        notification.onOpen({
          message:
            error?.response?.data?.message === undefined
              ? "Something went wrong"
              : error?.response?.data?.message,
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const updateContract = useMutation(
    "update-contract",
    contractService.updateContract,
    {
      onSuccess: (response) => {
        notification.onOpen({
          message:
            response.message === ""
              ? "Contract was successfully updated!"
              : response.message,
          type: "success",
          position: "top",
        });
        navigate("/app/contract");
      },
      onError: (error: any) => {
        notification.onOpen({
          message:
            error?.response?.data?.message === undefined
              ? "Something went wrong"
              : error?.response?.data?.message,
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const schema = yup.object().shape({
    nama: yup.string().required("nama is a required field"),
    pic: yup.string().required("pic is a required field"),
    email: yup.string().required("email is a required field"),
    end_date: yup.string().required("end_date is a required field"),
    status: yup.string().required("status is a required field"),
  });

  const form = useForm({
    defaultValues: {
      nama: "",
      kota: "",
      pic: "",
      contact: "",
      email: "",
      start_date: "",
      probation_date: "",
      end_date: "",
      status: "",
      pdf_file: "",
      admin_name: user.username,
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (id) {
      contractService
        .getContractById(Number(id))
        .then(({ data }) => {
          form.setValue("nama", get(data, "nama"));
          form.setValue("kota", get(data, "kota"));
          form.setValue("pic", get(data, "pic"));
          form.setValue("contact", get(data, "contact"));
          form.setValue("email", get(data, "email"));
          form.setValue("start_date", get(data, "start_date"));
          form.setValue("probation_date", get(data, "probation_date"));
          form.setValue("end_date", get(data, "end_date"));
          form.setValue("status", get(data, "status"));
          form.setValue("pdf_file", get(data, "pdf_file"));
        })
        .catch((error) => {
          console.log(error);
          notification.onOpen({
            message: "Error get data",
            type: "error",
            position: "top",
          });
        });
    }
  }, [id]);

  const onSubmit = (data: any) => {
    let errors = "";
    if (Object.keys(errors).length > 0) {
      notification.onOpen({
        message: errors,
        type: "error",
        position: "top",
      });
      return;
    }
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (key === "pdf_file" && pdfFile.length > 0) {
        formData.append("contract_pdf", pdfFile[0]);
      } else if (key === "start_date" || key === "end_date") {
        formData.append(
          key,
          moment(dayjs(data[key]).toDate()).format("YYYY-MM-DD")
        );
      } else {
        formData.append(key, data[key]);
      }
    });
    if (id) {
      formData.append("id", id);
      updateContract.mutate(formData);
    } else {
      createContract.mutate(formData);
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    if (event.target.files) {
      const fileList = Array.from(event.target.files);
      if (field === "pdf_file") {
        setPdfFile(fileList);
        form.setValue("pdf_file", event.target.files[0].name);
      }
    }
  };

  return (
    <MainLayout>
      <Breadcrumb
        label={id ? "Edit Contract" : "Create Contract"}
        breadcrumbs={[{ label: "Contract List", href: "/app/contract" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 5, 6]}>
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required={true}>
                  Nama
                </FormLabel>
                <Controller
                  name={"nama"}
                  control={form.control}
                  render={({ field: inputProps }) => {
                    return (
                      <TextField
                        {...inputProps}
                        type={"text"}
                        fullWidth
                      />
                    )
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required={true}>
                  Kota
                </FormLabel>
                <Controller
                  name={"kota"}
                  control={form.control}
                  render={({ field: inputProps }) => {
                    return (
                      <TextField
                        {...inputProps}
                        type={"text"}
                        fullWidth
                      />
                    )
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required={true}>
                  PIC
                </FormLabel>
                <Controller
                  name={"pic"}
                  control={form.control}
                  render={({ field: inputProps }) => {
                    return (
                      <TextField
                        {...inputProps}
                        type={"text"}
                        fullWidth
                      />
                    )
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required={true}>
                  Contact
                </FormLabel>
                <Controller
                  name={"contact"}
                  control={form.control}
                  render={({ field: inputProps }) => {
                    return (
                      <TextField
                        {...inputProps}
                        type={"text"}
                        fullWidth
                      />
                    )
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required={true}>
                  Email
                </FormLabel>
                <Controller
                  name={"email"}
                  control={form.control}
                  render={({ field: inputProps }) => {
                    return (
                      <TextField
                        {...inputProps}
                        type={"text"}
                        fullWidth
                      />
                    )
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required={true}>
                  Start Date
                </FormLabel>
                <Controller
                  name={"start_date"}
                  control={form.control}
                  render={({ field: inputProps }) => {
                    return (
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                      >
                        <DatePicker
                          {...inputProps}
                          onChange={(date: any) =>
                            form.setValue("start_date", date)
                          }
                          renderInput={(params) => (
                            <TextField {...params} fullWidth />
                          )}
                        />
                      </LocalizationProvider>
                    )
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required={false}>
                  Probation Date
                </FormLabel>
                <Controller
                  name={"probation_date"}
                  control={form.control}
                  render={({ field: inputProps }) => {
                    return (
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                      >
                        <DatePicker
                          {...inputProps}
                          onChange={(date: any) =>
                            form.setValue("probation_date", date)
                          }
                          renderInput={(params) => (
                            <TextField {...params} fullWidth />
                          )}
                        />
                      </LocalizationProvider>
                    )
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required={true}>
                  End Date
                </FormLabel>
                <Controller
                  name={"end_date"}
                  control={form.control}
                  render={({ field: inputProps }) => {
                    return (
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                      >
                        <DatePicker
                          {...inputProps}
                          onChange={(date: any) =>
                            form.setValue("end_date", date)
                          }
                          renderInput={(params) => (
                            <TextField {...params} fullWidth />
                          )}
                        />
                      </LocalizationProvider>
                    )
                  }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Status</FormLabel>
                <Controller
                  name="status"
                  control={form.control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={form.getValues("status")}
                      fullWidth
                      defaultValue={form.getValues("status")}
                    >
                      <MenuItem value="">Select Type</MenuItem>
                      <MenuItem key={'2months'} value={'2months'}>
                        2 Months
                      </MenuItem>
                      <MenuItem key={'probation'} value={'probation'}>
                        Probation (3 months)
                      </MenuItem>
                      <MenuItem key={'6months'} value={'6months'}>
                        6 months
                      </MenuItem>
                      <MenuItem key={'1year'} value={'1year'}>
                        1 Year
                      </MenuItem>
                      <MenuItem key={'2years'} value={'2years'}>
                        2 Years
                      </MenuItem>
                      <MenuItem key={'lifetime'} value={'lifetime'}>
                        Lifetime
                      </MenuItem>
                      <MenuItem key={'closed'} value={'closed'}>
                        Closed
                      </MenuItem>
                    </Select>
                  )}
                />
                <FormHelperText error>
                  {form.formState.errors.status?.message}
                </FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required={true}>
                  PDF File
                </FormLabel>
                <Controller
                  name={"pdf_file"}
                  control={form.control}
                  render={({ field: inputProps }) => {
                    return (
                      <>
                        <input
                          type="file"
                          name="pdf_file"
                          onChange={(event) =>
                            handleFileChange(event, "pdf_file")
                          }
                          hidden
                        />
                        <FormHelperText>
                          <strong>MoU</strong>
                        </FormHelperText>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            let element: HTMLElement =
                              document.querySelector(
                                `input[name="pdf_file"]`
                              )!;
                            element.click();
                          }}
                        >
                          Upload File
                        </Button>
                        <FormHelperText variant="filled">
                          {pdfFile[0]?.name}
                        </FormHelperText>
                      </>
                    )
                  }}
                />
              </FormControl>

              <Stack direction="row" spacing={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={form.handleSubmit(onSubmit)}
                >
                  SUBMIT
                </Button>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  CANCEL
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
